import { useRef, useState, useEffect } from "react";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { useLanguage } from "@/lib/LanguageContext";

const TESTIMONIALS = [
  {
    id: 1,
    name: "Sophie M.",
    country: "Deutschland",
    rating: 5,
    date: "Oktober 2025",
    listing: "Elegantes 2-Zimmer-Atelier",
    text: "Absolut traumhaft! Die Wohnung war noch schöner als auf den Fotos. Alles war blitzsauber, modern eingerichtet und die Lage war perfekt. Der Kontakt mit dem Gastgeber war super freundlich und schnell. Wir kommen definitiv wieder!",
    avatar: "SM"
  },
  {
    id: 2,
    name: "James R.",
    country: "United Kingdom",
    rating: 5,
    date: "November 2025",
    listing: "103m² Design-Apartment",
    text: "What a fantastic stay! The apartment is spacious, beautifully designed and has everything you need. The location near Messe Wien is ideal - tram stops right outside. Lukas was an excellent host, very responsive and helpful. Highly recommended!",
    avatar: "JR"
  },
  {
    id: 3,
    name: "Marie L.",
    country: "France",
    rating: 5,
    date: "Dezember 2025",
    listing: "Gemütliches Apartment mit Garten",
    text: "Un séjour parfait à Vienne! L'appartement est magnifique avec un jardin privé - idéal pour se détendre après une journée de visites. Tout était propre et bien équipé. La communication avec l'hôte était excellente. Je recommande vivement!",
    avatar: "ML"
  },
  {
    id: 4,
    name: "Thomas K.",
    country: "Österreich",
    rating: 5,
    date: "Jänner 2026",
    listing: "Elegantes 2-Zimmer-Atelier",
    text: "Perfekte Unterkunft für unseren Wien-Trip! Stilvolle Einrichtung, mega sauber und die Küche hat alles was man braucht. Die Check-in Anweisungen waren klar und unkompliziert. Preis-Leistung ist top. Klare Empfehlung!",
    avatar: "TK"
  },
  {
    id: 5,
    name: "Elena V.",
    country: "Italia",
    rating: 5,
    date: "Februar 2026",
    listing: "103m² Design-Apartment",
    text: "Appartamento spazioso e moderno, perfetto per famiglie. La posizione è ottima, vicino ai trasporti pubblici. I proprietari sono stati molto gentili e disponibili. Vienna è una città meravigliosa e questo appartamento è il posto ideale dove soggiornare!",
    avatar: "EV"
  },
  {
    id: 6,
    name: "Anna & Peter",
    country: "Schweiz",
    rating: 5,
    date: "März 2026",
    listing: "Gemütliches Apartment mit Garten",
    text: "Wir waren zu viert und hatten reichlich Platz. Die Terrasse mit Garten ist ein absoluter Traum - morgens dort Kaffee trinken ist unbezahlbar. Alles war top gepflegt und die Ausstattung hochwertig. Bestes Airbnb das wir je hatten!",
    avatar: "AP"
  }
];

function useInView(ref, threshold = 0.2) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref, threshold]);
  return visible;
}

export default function Testimonials() {
  const { lang } = useLanguage();
  const sectionRef = useRef(null);
  const visible = useInView(sectionRef);
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
    // Compute active dot for mobile indicator
    const cards = el.querySelectorAll("[data-card]");
    if (cards.length) {
      const cardW = cards[0].getBoundingClientRect().width + 16; // +gap
      const idx = Math.round(el.scrollLeft / cardW);
      setActiveIdx(Math.min(idx, TESTIMONIALS.length - 1));
    }
  };

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const cards = el.querySelectorAll("[data-card]");
    const step = cards.length ? cards[0].getBoundingClientRect().width + 16 : 320;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
    setTimeout(checkScroll, 400);
  };

  useEffect(() => { checkScroll(); }, []);

  return (
    <section
      ref={sectionRef}
      data-testid="testimonials-section"
      className="snap-section md:min-h-screen flex items-center py-16 md:py-24 bg-[#FAFAFA] overflow-hidden"
    >
      <div className="w-full max-w-7xl mx-auto">
        {/* Header (with left padding on mobile so it aligns with the first card) */}
        <div
          className="text-center mb-10 md:mb-14 px-6 md:px-12 lg:px-24"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(30px)",
            transition: "all 0.8s cubic-bezier(0.25, 1, 0.5, 1)",
          }}
        >
          <p className="text-[10px] md:text-xs font-bold tracking-[0.2em] uppercase text-[#86868B] mb-3">
            {lang === "de" ? "Was unsere Gäste sagen" : "What our guests say"}
          </p>
          <h2
            className="text-3xl md:text-5xl font-semibold tracking-tighter text-[#1D1D1F]"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            {lang === "de" ? "Bewertungen" : "Reviews"}
          </h2>
        </div>

        {/* Scroll container (full-bleed on mobile so cards can swipe naturally) */}
        <div className="relative">
          {/* Navigation arrows — desktop only */}
          {canScrollLeft && (
            <button
              data-testid="testimonial-prev"
              onClick={() => scroll(-1)}
              className="hidden md:flex absolute left-6 lg:left-12 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white shadow-lg border border-black/5 items-center justify-center text-[#1D1D1F] hover:bg-black hover:text-white transition-all"
              aria-label="Previous review"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
          )}
          {canScrollRight && (
            <button
              data-testid="testimonial-next"
              onClick={() => scroll(1)}
              className="hidden md:flex absolute right-6 lg:right-12 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white shadow-lg border border-black/5 items-center justify-center text-[#1D1D1F] hover:bg-black hover:text-white transition-all"
              aria-label="Next review"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}

          {/* Cards row — px-6 leading edge on mobile + pr-6 trailing so last card has breathing room */}
          <div
            ref={scrollRef}
            onScroll={checkScroll}
            className="flex gap-4 md:gap-6 overflow-x-auto scrollbar-hide pb-4 snap-x snap-mandatory px-6 md:px-12 lg:px-24"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}
          >
            {TESTIMONIALS.map((review, i) => (
              <div
                key={review.id}
                data-card="true"
                data-testid={`testimonial-card-${review.id}`}
                className="snap-center shrink-0 w-[82vw] sm:w-[340px] md:w-[380px]"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(40px)",
                  transition: `all 0.7s cubic-bezier(0.25, 1, 0.5, 1) ${i * 80}ms`,
                }}
              >
                <div className="h-full bg-white rounded-3xl border border-black/5 p-5 md:p-7 shadow-[0_4px_20px_rgba(0,0,0,0.04)] flex flex-col">
                  {/* Quote icon */}
                  <Quote className="w-7 h-7 md:w-8 md:h-8 text-[#E5E5E5] mb-3 md:mb-4 shrink-0" />

                  {/* Text */}
                  <p className="text-[13px] md:text-sm text-[#1D1D1F] leading-relaxed flex-1 mb-4 md:mb-5">
                    &ldquo;{review.text}&rdquo;
                  </p>

                  {/* Rating */}
                  <div className="flex items-center gap-0.5 mb-3 md:mb-4">
                    {Array.from({ length: review.rating }, (_, si) => (
                      <Star key={`${review.id}-star-${si}`} className="w-3.5 h-3.5 md:w-4 md:h-4 fill-[#FFB800] text-[#FFB800]" />
                    ))}
                  </div>

                  {/* Author */}
                  <div className="flex items-center gap-3 pt-4 border-t border-black/5">
                    <div className="w-10 h-10 rounded-full bg-[#1D1D1F] flex items-center justify-center text-white text-xs font-bold shrink-0">
                      {review.avatar}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#1D1D1F] truncate">{review.name}</p>
                      <p className="text-xs text-[#86868B] truncate">{review.country} &middot; {review.date}</p>
                    </div>
                  </div>

                  {/* Listing */}
                  <p className="mt-3 text-[10px] font-medium tracking-wider uppercase text-[#86868B] truncate">
                    {review.listing}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile dot indicator (hidden on desktop where arrows take over) */}
          <div data-testid="testimonials-dots" className="flex md:hidden justify-center gap-1.5 mt-2">
            {TESTIMONIALS.map((review, i) => (
              <span
                key={`dot-${review.id}`}
                className={`h-1.5 rounded-full transition-all duration-300 ${i === activeIdx ? "bg-[#1D1D1F] w-5" : "bg-[#1D1D1F]/25 w-1.5"}`}
              />
            ))}
          </div>
        </div>

        {/* Airbnb badge */}
        <div className="mt-8 md:mt-10 text-center px-6">
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 md:px-5 py-2 md:py-2.5 border border-black/5 shadow-sm max-w-full">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#FF5A5F" className="shrink-0">
              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.6 0 12 0zm5.7 17.4c-.6 1.1-1.8 1.8-3 1.8-.5 0-1-.1-1.4-.3-1.1-.5-2-1.3-3.2-2.7-1.2 1.4-2.1 2.2-3.2 2.7-.5.2-.9.3-1.4.3-1.3 0-2.5-.7-3-1.8-.7-1.3-.5-3 .5-5.1 .8-1.6 2-3.4 3.6-5.3.4-.5.8-.9 1.1-1.3.3-.3.5-.6.8-.8.4-.4.8-.6 1.3-.8.5-.2 1-.2 1.5 0 .5.2.9.4 1.3.8.3.2.5.5.8.8.3.4.7.8 1.1 1.3 1.6 1.9 2.8 3.7 3.6 5.3 1 2.1 1.2 3.8.5 5.1z"/>
            </svg>
            <span className="text-[11px] md:text-xs font-semibold text-[#1D1D1F]">
              {lang === "de" ? "Verifizierte Airbnb Bewertungen" : "Verified Airbnb Reviews"}
            </span>
            <span className="text-[11px] md:text-xs font-bold text-[#FF5A5F]">5.0</span>
          </div>
        </div>
      </div>
    </section>
  );
}
